import dayjs from 'dayjs';
import { ClientAuthToken, parseClientAuthTokenResponse } from 'hevy-shared';
import Cookies from 'js-cookie';
import API, { SocialLoginResponse } from 'utils/API';
import { fireAndForget } from 'utils/async';

export class Auth {
  private _signupProps?: {
    email: string;
    password: string;
    gympassUserId?: string;
    coachInviteShortId?: string;
  };

  afterVerificationSuccess?: () => Promise<void>;

  public get signupProps(): Readonly<typeof this._signupProps> {
    return this._signupProps;
  }

  get isAuthenticated(): boolean {
    return !!Cookies.get('auth2.0-token') || !!Cookies.get('auth-token');
  }

  login = async (
    usernameOrEmail: string,
    password: string,
    gympassUserId?: string,
    recaptchaToken?: string,
  ) => {
    const result = await API.login(usernameOrEmail, password, gympassUserId, recaptchaToken);
    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
  };

  sendSignupVerificationEmail = async (signupProps: {
    email: string;
    password: string;
    gympassUserId?: string;
    coachInviteShortId?: string;
  }) => {
    this._signupProps = signupProps;
    const result = await API.sendSignupVerificationEmail({ email: signupProps.email });
    return result.data;
  };

  resendSignupVerificationEmail = async () => {
    if (!this._signupProps) {
      throw new Error();
    }

    const result = await API.sendSignupVerificationEmail({ email: this._signupProps.email });
    return result.data;
  };

  signupWithVerifiedEmail = async ({
    email,
    password,
    gympassUserId,
    verificationCode,
  }: {
    email: string;
    password: string;
    gympassUserId?: string;
    verificationCode: string;
  }) => {
    const result = await API.signupWithVerifiedEmail({
      email,
      password,
      gympassUserId,
      verificationCode,
    });

    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
  };

  signupCoachClient = async ({
    email,
    password,
    recaptchaToken,
    gympassUserId,
    coachInviteShortId,
  }: {
    email: string;
    password: string;
    recaptchaToken: string;
    gympassUserId?: string;
    coachInviteShortId: string;
  }) => {
    const result = await API.signup({
      email,
      password,
      recaptchaToken,
      gympassUserId,
      coachInviteShortId,
    });

    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
  };

  loginWithGoogle = async (code: string, gympassUserId?: string): Promise<SocialLoginResponse> => {
    const result = await API.signInWithGoogle(code, gympassUserId);
    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
    return result.data;
  };

  signUpWithGoogle = async (code: string, gympassUserId?: string): Promise<SocialLoginResponse> => {
    const result = await API.signUpWithGoogle(code, gympassUserId);
    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
    return result.data;
  };

  loginWithApple = async ({
    identityToken,
    email,
    gympassUserId,
  }: {
    identityToken: string;
    email?: string;
    gympassUserId?: string;
  }): Promise<SocialLoginResponse> => {
    const result = await API.signInWithApple({ identityToken, email, gympassUserId });
    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
    return result.data;
  };

  signupWithApple = async ({
    identityToken,
    email,
    gympassUserId,
  }: {
    identityToken: string;
    email?: string;
    gympassUserId?: string;
  }): Promise<SocialLoginResponse> => {
    const result = await API.signUpWithApple({ identityToken, email, gympassUserId });
    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
    return result.data;
  };

  authMigrate = async () => {
    if (!!Cookies.get('auth2.0-token')) return;

    const result = await API.authMigrate();
    this.setAuthCookie(parseClientAuthTokenResponse(result.data));
  };

  logout = async () => {
    await API.clearAuthToken();

    Cookies.remove('auth2.0-token');
    Cookies.remove('access-token');
    Cookies.remove('auth-token');
  };

  userLogout = async () => {
    const isUsingNewAuth = !!Cookies.get('auth2.0-token');

    if (isUsingNewAuth) {
      await fireAndForget([API.logout()]);
    }

    await API.clearAuthToken();

    Cookies.remove('auth2.0-token');
    Cookies.remove('access-token');
    Cookies.remove('auth-token');
  };

  private setAuthCookie = (newToken: ClientAuthToken | null) => {
    const { access_token, refresh_token, expires_at } = newToken || {};

    const auth2_0Token =
      !!access_token && !!refresh_token && !!expires_at
        ? { access_token, refresh_token, expires_at: new Date(expires_at) }
        : null;

    Cookies.set('auth2.0-token', JSON.stringify(auth2_0Token), {
      expires: dayjs().add(10, 'year').toDate(),
    });

    if (access_token) {
      // This is used by the server side logic.
      Cookies.set('access-token', access_token, { expires: dayjs().add(10, 'year').toDate() });
    } else {
      Cookies.remove('access-token');
    }

    Cookies.remove('auth-token');
  };

  onNewAuthToken = (newToken: ClientAuthToken) => {
    Cookies.set('auth2.0-token', JSON.stringify(newToken), {
      expires: dayjs().add(10, 'year').toDate(),
    });

    Cookies.set('access-token', newToken.access_token, {
      expires: dayjs().add(10, 'year').toDate(),
    });
  };
}
